/**
 * Trainer Learning — unified trainer-side dashboard at /provider/me/learning.
 * Two tabs: Live Sessions (pricing config + bookings list) + Courses (CRUD + lessons).
 *
 * Designed so an approved trainer can publish their first session offering
 * AND upload their first course in <5 minutes.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { VideoCameraIcon as VideoCamera, FilmStripIcon as FilmStrip, PlusIcon as Plus, TrashIcon as Trash, UploadSimpleIcon as UploadSimple, CurrencyDollarIcon as CurrencyDollar, ClockIcon as Clock, StarIcon as Star, XIcon as XIcon, CheckCircleIcon as CheckCircle, ShieldCheckIcon as ShieldCheck, WalletIcon as Wallet, TrendUpIcon as TrendUp, BankIcon as Bank } from "@phosphor-icons/react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};
const $ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export default function TrainerLearning() {
  const [tab, setTab] = useState("sessions");
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-1">Trainer · Earn</p>
        <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Live Sessions & Courses</h1>
        <p className="text-sm text-[#666] mt-2 max-w-2xl">
          Charge clients for 1-on-1 live training sessions and sell pre-recorded video courses.
          Armstrong takes 20% of your first $5,000 a year, then 10% — money is routed to your Stripe Connect account.
        </p>
        <div className="flex gap-2 mt-6 border-b border-[#E5E5E5] flex-wrap">
          {[
            { key: "sessions", label: "Live Sessions", icon: VideoCamera },
            { key: "courses",  label: "Pre-recorded Courses", icon: FilmStrip },
            { key: "earnings", label: "Earnings", icon: CurrencyDollar },
            { key: "disclosures", label: "Trainer Profile", icon: ShieldCheck },
          ].map((t) => {
            const I = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tl-tab-${t.key}`}
                className={"inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors " +
                  (active ? "border-[#C1272D] text-ink font-semibold" : "border-transparent text-[#666] hover:text-ink")}
              >
                <I size={16} /> {t.label}
              </button>
            );
          })}
        </div>
        {tab === "sessions" && <SessionsTab />}
        {tab === "courses" && <CoursesTab />}
        {tab === "earnings" && <EarningsTab />}
        {tab === "disclosures" && <DisclosuresTab />}
      </div>
    </AppShell>
  );
}

// ─────────────────── Live Sessions tab ────────────────────
function SessionsTab() {
  const [cfg, setCfg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/trainer-sessions/me/config"),
      api.get("/trainer-sessions/me/trainer"),
    ]).then(([c, s]) => {
      setCfg(c.data);
      setSessions(s.data?.sessions || []);
    }).catch((e) => {
      if (e?.response?.status === 403) {
        toast.error("Only approved trainers can access this page.");
      }
    });
  };
  useEffect(load, []);

  const updateTier = (i, key, value) => {
    setCfg((c) => {
      const tiers = [...c.tiers];
      tiers[i] = { ...tiers[i], [key]: value };
      return { ...c, tiers };
    });
  };
  const removeTier = (i) => setCfg((c) => ({ ...c, tiers: c.tiers.filter((_, j) => j !== i) }));
  const addTier = () => setCfg((c) => ({
    ...c,
    tiers: [...c.tiers, { label: "New tier", sessions: 1, price_cents: 5000, duration_min: 45 }],
  }));

  const save = async () => {
    if (!cfg.tiers.length) { toast.error("At least one pricing tier is required"); return; }
    setSaving(true);
    try {
      await api.put("/trainer-sessions/me/config", {
        tiers: cfg.tiers,
        first_session_free: cfg.first_session_free,
        weekly_availability: cfg.weekly_availability || {},
        cancellation_window_hours: cfg.cancellation_window_hours || 24,
      });
      toast.success("Saved — your offering is live on your provider profile.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="mt-8 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;

  return (
    <div className="mt-6">
      {/* Pricing tiers */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6">
        <h2 className="font-spectral text-xl">Pricing tiers</h2>
        <p className="text-sm text-[#666] mt-1">Up to 3 packages. Most trainers offer "1 session / 5-pack / 10-pack".</p>
        <div className="space-y-3 mt-5">
          {cfg.tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,80px,140px,100px,auto] gap-2 items-end" data-testid={`tl-tier-${i}`}>
              <div>
                <label className="text-xs text-[#666]">Label</label>
                <Input value={t.label} onChange={(e) => updateTier(i, "label", e.target.value)} data-testid={`tl-tier-label-${i}`} />
              </div>
              <div>
                <label className="text-xs text-[#666]"># sessions</label>
                <Input type="number" min={1} value={t.sessions} onChange={(e) => updateTier(i, "sessions", Number(e.target.value))} data-testid={`tl-tier-sessions-${i}`} />
              </div>
              <div>
                <label className="text-xs text-[#666]">Price (cents)</label>
                <Input type="number" min={1500} value={t.price_cents} onChange={(e) => updateTier(i, "price_cents", Number(e.target.value))} data-testid={`tl-tier-price-${i}`} />
              </div>
              <div>
                <label className="text-xs text-[#666]">Mins/session</label>
                <Input type="number" min={15} max={180} value={t.duration_min} onChange={(e) => updateTier(i, "duration_min", Number(e.target.value))} data-testid={`tl-tier-duration-${i}`} />
              </div>
              <button type="button" onClick={() => removeTier(i)} className="p-2 text-[#C1272D] hover:bg-[#C1272D]/10 rounded-md self-end" data-testid={`tl-tier-remove-${i}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
        {cfg.tiers.length < 3 && (
          <Button variant="outline" size="sm" className="mt-3" onClick={addTier} data-testid="tl-tier-add">
            <Plus size={14} className="mr-1.5" /> Add tier
          </Button>
        )}
        <label className="flex items-center justify-between gap-3 mt-5 pt-5 border-t border-[#EEE] text-sm">
          <span>First session free (promo)</span>
          <Switch checked={!!cfg.first_session_free} onCheckedChange={(v) => setCfg({ ...cfg, first_session_free: v })} data-testid="tl-first-free" />
        </label>
        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={saving} data-testid="tl-save-config">
            {saving ? "Saving…" : (cfg.active ? "Save changes" : "Publish offering")}
          </Button>
        </div>
      </div>

      {/* Bookings */}
      <div className="mt-8">
        <h2 className="font-spectral text-xl mb-3">Upcoming & past sessions</h2>
        {sessions.length === 0 ? (
          <p className="text-sm text-[#666] bg-white border border-[#E5E5E5] rounded-xl p-6 text-center">
            No bookings yet. Once you publish your offering, clients can book from your provider profile.
          </p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => <SessionRow key={s.id} s={s} mine="trainer" />)}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionRow({ s, mine }) {
  const canJoin = s.status === "confirmed" && new Date(s.scheduled_at) - Date.now() < 5 * 60 * 1000;
  const upcoming = new Date(s.scheduled_at) > Date.now();
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`tl-session-${s.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-spectral text-base">{s.tier_label}</p>
        <p className="text-xs text-[#888]">{fmt(s.scheduled_at)} · {s.duration_min} min · {$(s.gross_cents)} · {s.status}</p>
      </div>
      {canJoin && <a href={`/sessions/${s.id}/join`} className="text-sm bg-[#0E0E0E] text-white rounded-full px-3 py-1.5 hover:bg-[#C1272D]" data-testid={`tl-session-join-${s.id}`}>Join now →</a>}
      {!canJoin && upcoming && <span className="text-xs text-[#888]">Opens 5 min before</span>}
    </div>
  );
}

// ─────────────────── Courses tab ────────────────────
function CoursesTab() {
  const [courses, setCourses] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/trainer-courses/me").then((r) => setCourses(r.data.courses || [])).catch(() => setCourses([]));
  useEffect(load, []);

  const create = async () => {
    const title = window.prompt("Course title");
    if (!title?.trim()) return;
    try {
      const r = await api.post("/trainer-courses/me", {
        title: title.trim(),
        flat_price_cents: 4900,
        pricing_model: "flat_fee",
      });
      toast.success("Course created — add lessons next");
      setEditing(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (courses === null) return <div className="mt-8 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#666]">{courses.length} course{courses.length === 1 ? "" : "s"}</p>
        <Button onClick={create} data-testid="tl-course-new"><Plus size={14} className="mr-1.5" /> New course</Button>
      </div>
      {courses.length === 0 ? (
        <p className="text-sm text-[#666] bg-white border border-[#E5E5E5] rounded-xl p-8 text-center">
          Your first course can be ready in 15 minutes. Click "New course" above, set a price, then upload your lesson videos one at a time.
        </p>
      ) : (
        <div className="space-y-2">
          {courses.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3" data-testid={`tl-course-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-spectral text-base truncate">{c.title}</p>
                <p className="text-xs text-[#888]">{c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"} · {$(c.flat_price_cents)} · {c.published ? "Published" : "Draft"}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`tl-course-edit-${c.id}`}>Edit</Button>
            </div>
          ))}
        </div>
      )}
      {editing && <CourseEditor course={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CourseEditor({ course, onClose }) {
  const [form, setForm] = useState(course);
  const [lessons, setLessons] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const coverRef = useRef(null);

  const load = () => api.get("/trainer-courses/me").then((r) => {
    const c = (r.data.courses || []).find((x) => x.id === course.id);
    if (c) setForm(c);
  });

  // Load lessons via the public by-slug endpoint
  useEffect(() => {
    if (!form?.slug) return;
    api.get(`/trainer-courses/by-slug/${form.slug}`).catch(() => null).then((r) => {
      if (r?.data?.lessons) setLessons(r.data.lessons);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.slug, form?.lesson_count]);

  const save = async (publishFlag) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, subtitle: form.subtitle || "", description: form.description || "",
        cover_url: form.cover_url || "", pricing_model: form.pricing_model,
        flat_price_cents: Number(form.flat_price_cents), published: publishFlag,
      };
      const r = await api.put(`/trainer-courses/me/${course.id}`, payload);
      setForm(r.data);
      toast.success(publishFlag ? "Published" : "Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLesson = async (file) => {
    if (!file) return;
    const title = window.prompt("Lesson title", `Lesson ${lessons.length + 1}`);
    if (!title?.trim()) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("sort_order", String(lessons.length));
    fd.append("preview", "false");
    try {
      toast.message("Uploading… (large videos take a minute)");
      await api.post(`/trainer-courses/me/${course.id}/lessons`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Lesson added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/trainer-courses/me/${course.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_url: r.data.cover_url }));
      toast.success("Cover updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const deleteLesson = async (lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await api.delete(`/trainer-courses/me/${course.id}/lessons/${lid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-3xl mx-auto bg-white rounded-2xl p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="tl-course-editor">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F4F4F4]" data-testid="tl-course-editor-close">
          <XIcon size={18} />
        </button>
        <h2 className="font-spectral text-2xl">{form.title}</h2>
        <p className="text-xs text-[#888]">/courses/{form.slug}</p>

        <div className="mt-5 space-y-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="tl-course-title" />
          <Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="One-line tagline" data-testid="tl-course-subtitle" />
          <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description" data-testid="tl-course-description" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#666]">Price (cents)</label>
              <Input type="number" min={0} max={49900} value={form.flat_price_cents} onChange={(e) => setForm({ ...form, flat_price_cents: Number(e.target.value) })} data-testid="tl-course-price" />
            </div>
            <div>
              <label className="text-xs text-[#666]">Pricing model</label>
              <select value={form.pricing_model} onChange={(e) => setForm({ ...form, pricing_model: e.target.value })} className="w-full rounded-md border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="tl-course-pricing-model">
                <option value="flat_fee">Flat fee (buyer owns forever)</option>
                <option value="buyer_pass">Buyer Pass only ($0.40/watch royalty)</option>
                <option value="both">Both (buyer chooses)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#666]">Cover image</label>
            <div className="flex items-center gap-3">
              {form.cover_url && <img src={resolveUrl(form.cover_url)} alt="" className="h-14 w-14 rounded-md object-cover" />}
              <Button size="sm" variant="outline" onClick={() => coverRef.current?.click()} data-testid="tl-course-cover-btn">
                <UploadSimple size={14} className="mr-1.5" /> {form.cover_url ? "Replace cover" : "Upload cover"}
              </Button>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="tl-course-cover-input" />
            </div>
          </div>
        </div>

        {/* Lessons */}
        <div className="mt-8 border-t border-[#EEE] pt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-spectral text-lg">Lessons ({lessons.length})</h3>
            <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="tl-lesson-add">
              <Plus size={14} className="mr-1" /> Upload lesson
            </Button>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadLesson(e.target.files?.[0])} data-testid="tl-lesson-input" />
          </div>
          {lessons.length === 0 ? (
            <p className="text-sm text-[#888] italic">No lessons yet. Upload at least one before publishing.</p>
          ) : (
            <div className="space-y-2">
              {lessons.map((l, i) => (
                <div key={l.id} className="flex items-center gap-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-md p-3" data-testid={`tl-lesson-${l.id}`}>
                  <span className="text-xs text-[#888] w-6">{i + 1}</span>
                  <p className="flex-1 truncate text-sm">{l.title}</p>
                  {l.preview && <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Preview</span>}
                  <button onClick={() => deleteLesson(l.id)} className="text-[#C1272D]" data-testid={`tl-lesson-delete-${l.id}`}><Trash size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 mt-8 pt-5 border-t border-[#EEE]">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="tl-course-save-draft">Save as draft</Button>
          <Button onClick={() => save(true)} disabled={saving || lessons.length === 0} data-testid="tl-course-publish">
            <CheckCircle size={14} className="mr-1.5" /> {form.published ? "Save & keep published" : "Publish course"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Earnings tab ────────────────────
function EarningsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/trainer-sessions/me/payouts")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load earnings"));
  }, []);
  if (err) return <p className="mt-6 text-sm text-[#C1272D]" data-testid="tl-earnings-err">{err}</p>;
  if (!data) return <div className="mt-6 grid sm:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;

  const cards = [
    { key: "this_month", label: "This month", I: TrendUp },
    { key: "last_30_days", label: "Last 30 days", I: Wallet },
    { key: "last_12_months", label: "Last 12 months", I: CurrencyDollar },
    { key: "lifetime", label: "Lifetime", I: Bank },
  ];

  return (
    <div className="mt-6 space-y-5" data-testid="tl-earnings-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(({ key, label, I }) => {
          const w = data[key];
          return (
            <div key={key} className="bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid={`tl-earnings-${key}`}>
              <p className="text-[11px] uppercase tracking-wider text-[#888] flex items-center gap-1.5">
                <I size={12} weight="duotone" className="text-gold" /> {label}
              </p>
              <p className="font-spectral text-3xl tracking-tight mt-1.5">{$(w.net_cents)}</p>
              <p className="text-[11px] text-[#888] mt-1">net of {$(w.fee_cents)} platform fee</p>
              <p className="text-[11px] text-[#666] mt-2">
                Sessions {$(w.sessions_net_cents)} · Courses {$(w.courses_net_cents)}
              </p>
            </div>
          );
        })}
      </div>
      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5">
        <p className="font-spectral text-xl tracking-tight">Payouts via Stripe</p>
        <p className="text-sm text-[#666] mt-1.5 leading-relaxed">
          Your Stripe Connect account is the source of truth for actual bank movements. Stripe pays out on its standard schedule (typically 2 business days after settlement) and issues your 1099-K directly. The summary above shows Armstrong-side gross + platform fee aggregated across live sessions and courses.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-bold " + (data.stripe_payouts_enabled ? "bg-[#0F8043] text-white" : "bg-[#F2F2F2] text-[#555]")}>
            {data.stripe_payouts_enabled ? "Payouts enabled" : "Payouts pending"}
          </span>
          {!data.stripe_payouts_enabled && (
            <Link to="/me/provider" className="underline text-[#C1272D]" data-testid="tl-earnings-finish-stripe">
              Finish Stripe Connect onboarding →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Disclosures tab ────────────────────
const METHOD_OPTIONS = [
  { key: "positive_reinforcement", label: "Positive reinforcement (R+)" },
  { key: "clicker", label: "Clicker training" },
  { key: "balanced", label: "Balanced (R+/R-)" },
  { key: "lima", label: "LIMA (Least Intrusive, Minimally Aversive)" },
  { key: "force_free", label: "Force-free" },
  { key: "obedience_traditional", label: "Traditional obedience" },
  { key: "behavior_modification", label: "Behavior modification" },
  { key: "agility_sport", label: "Agility / dog sports" },
];
const TOOL_OPTIONS = [
  { key: "clicker", label: "Clicker" },
  { key: "treats", label: "Food rewards / treats" },
  { key: "flat_collar", label: "Flat collar" },
  { key: "harness", label: "Front-clip harness" },
  { key: "long_line", label: "Long line / leash" },
  { key: "head_halter", label: "Head halter (Halti, Gentle Leader)" },
  { key: "prong_collar", label: "Prong collar" },
  { key: "slip_lead", label: "Slip lead / choke chain" },
  { key: "e_collar", label: "Electronic / e-collar" },
  { key: "muzzle_training", label: "Muzzle training" },
];

function DisclosuresTab() {
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/providers/mine")
      .then((r) => {
        setProvider(r.data);
        setForm({
          training_methods: r.data.training_methods || [],
          tools_used: r.data.tools_used || [],
          years_experience: r.data.years_experience ?? "",
          certifications_detail: r.data.certifications_detail || [],
          liability_insurance_held: r.data.liability_insurance_held ?? false,
          disclosure_no_animal_cruelty_history: r.data.disclosure_no_animal_cruelty_history ?? false,
          disclosure_no_open_consumer_complaints: r.data.disclosure_no_open_consumer_complaints ?? false,
          disclosures_detail: r.data.disclosures_detail || "",
        });
      })
      .catch(() => setForm({}));
  }, []);

  const toggle = (field, key) => {
    const cur = new Set(form[field] || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setForm({ ...form, [field]: Array.from(cur) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/providers/mine", {
        ...form,
        years_experience: form.years_experience === "" ? null : parseInt(form.years_experience, 10),
      });
      toast.success("Disclosures saved and published.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="mt-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  if (provider && !["trainer", "behaviorist"].includes(provider.kind)) {
    return <p className="mt-6 text-sm text-[#666]">Disclosures apply only to trainer and behaviorist providers.</p>;
  }

  return (
    <div className="mt-6 space-y-5" data-testid="tl-disclosures-panel">
      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5">
        <p className="font-spectral text-xl tracking-tight">Why these matter</p>
        <p className="text-sm text-[#666] mt-1.5 leading-relaxed">
          Most US states don't license dog trainers, but every state has consumer-protection statutes against deceptive advertising. These disclosures are sworn self-attestations that we publish on your public profile so buyers can make an informed decision. Armstrong does <strong>not</strong> verify them — but lying here is grounds for account suspension and may expose you to claims under your state's consumer-protection statute.
        </p>
      </div>

      <Pickset
        title="Training methods you use"
        subtitle="Pick all that apply. These appear publicly on your profile."
        options={METHOD_OPTIONS}
        selected={form.training_methods}
        onToggle={(k) => toggle("training_methods", k)}
        testid="tl-methods"
      />
      <Pickset
        title="Tools you use"
        subtitle="Buyers care about prong collars, e-collars, and slip leads. Being honest here is the #1 trust-builder."
        options={TOOL_OPTIONS}
        selected={form.tools_used}
        onToggle={(k) => toggle("tools_used", k)}
        testid="tl-tools"
      />

      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#888]">Years of experience</label>
          <Input
            type="number" min="0" max="80"
            value={form.years_experience}
            onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
            data-testid="tl-years"
          />
        </div>
        <label className="flex items-start gap-3 cursor-pointer pt-5 sm:pt-7" data-testid="tl-insurance">
          <input
            type="checkbox"
            checked={!!form.liability_insurance_held}
            onChange={(e) => setForm({ ...form, liability_insurance_held: e.target.checked })}
            className="mt-1"
          />
          <span className="text-[13px] text-[#3F3F3F]">
            <strong>I carry professional liability insurance.</strong> Strongly recommended for training in person.
          </span>
        </label>
      </div>

      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5 space-y-3">
        <p className="font-spectral text-xl tracking-tight">Sworn consumer disclosures</p>
        <p className="text-xs text-[#666]">
          Each statement is a sworn self-attestation. If any answer is "no", explain in the disclosure box below; we publish your explanation verbatim on your public profile.
        </p>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="tl-discl-cruelty">
          <input type="checkbox" checked={!!form.disclosure_no_animal_cruelty_history} onChange={(e) => setForm({ ...form, disclosure_no_animal_cruelty_history: e.target.checked })} className="mt-1" />
          <span className="text-[13px] text-[#3F3F3F]">I have <strong>no history</strong> of animal-cruelty convictions, citations, or pending charges.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="tl-discl-complaints">
          <input type="checkbox" checked={!!form.disclosure_no_open_consumer_complaints} onChange={(e) => setForm({ ...form, disclosure_no_open_consumer_complaints: e.target.checked })} className="mt-1" />
          <span className="text-[13px] text-[#3F3F3F]">I have <strong>no open consumer-protection complaints, BBB complaints, or active lawsuits</strong> filed by clients regarding my training services.</span>
        </label>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#888]">Additional disclosures (optional, public)</label>
          <Textarea
            rows={3}
            placeholder="E.g. 'I trained at Karen Pryor Academy in 2019' or 'Resolved BBB complaint #12345 in 2023'."
            value={form.disclosures_detail}
            onChange={(e) => setForm({ ...form, disclosures_detail: e.target.value })}
            data-testid="tl-discl-detail"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white font-semibold"
          data-testid="tl-save-disclosures"
        >
          {saving ? "Saving…" : "Publish disclosures"}
        </Button>
      </div>
    </div>
  );
}

function Pickset({ title, subtitle, options, selected, onToggle, testid }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5">
      <p className="font-spectral text-xl tracking-tight">{title}</p>
      {subtitle && <p className="text-xs text-[#666] mt-1">{subtitle}</p>}
      <div className="mt-3 flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const on = (selected || []).includes(o.key);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onToggle(o.key)}
              data-testid={`${testid}-${o.key}`}
              className={"rounded-full px-3 py-1.5 text-[12.5px] border transition-colors " +
                (on ? "bg-[#0E0E0E] text-white border-[#0E0E0E]" : "bg-white text-ink border-[#E5E5E5] hover:border-[#0E0E0E]")}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

